import React, { useRef } from 'react';
import { createPortal } from 'react-dom';
import ReactTooltip from 'react-tooltip';
import cn from 'class-names';

import ActionsTooltipContent from '@uikit/old/ActionsTooltipContent/ActionsTooltipContent';
import { SVGIcon, SvgIconType } from '@uikit/old/Icon/Icon';
import { ActionsMenuPropsType } from '@components/ActionsMenu/ActionsMenu.types';

import './ActionsMenu.scss';

/** The 3-dot (kebab) trigger. Only this icon gets the darker default fill, custom icons keep their own colors. */
const DEFAULT_ACTIONS_ICON: SvgIconType = 'actions';

function ActionsMenu({
  id, // for tooltip
  itemId,
  onRemove,
  onRename,
  actionsConfig,
  iconClassName = '',
  tooltipClassName = '',
  tooltipPlace = 'top',
  children = null,
  itemTitle = '',
  catchEvents = true,
  isSingleStepRemove = false,
  icon = DEFAULT_ACTIONS_ICON,
  portalSelector = '',
  iconSize,
  removeActionTitle,
  testId,
}: ActionsMenuPropsType): JSX.Element {
  const tooltipRef = useRef();

  const handleClick = (e) => {
    if (catchEvents) {
      e.stopPropagation();
    }

    ReactTooltip.show(tooltipRef.current);
  };

  const handleHideTooltip = () => {
    // Manualy closing tooltip with mouse in it
    // @ts-ignore
    tooltipRef!.current.mouseOnToolTip = () => false;
    ReactTooltip.hide(tooltipRef.current);
  };

  const cnActionsMenu = cn('actions-menu', {
    'actions-menu--kebab': icon === DEFAULT_ACTIONS_ICON,
  });

  const cnActionsIcon = cn('actions-menu__icon', {
    [iconClassName]: iconClassName.length !== 0,
  });

  const getTooltip = () => {
    return (
      <ReactTooltip
        ref={tooltipRef}
        className={tooltipClassName || 'react-tooltip react-tooltip--actions'}
        id={String(id)}
        place={tooltipPlace}
        effect="solid"
        event="click"
        globalEventOff="click"
        arrowColor="transparent"
        clickable
      >
        {children || (
          <ActionsTooltipContent
            itemId={itemId}
            itemTitle={itemTitle}
            onRename={onRename}
            onRemove={onRemove}
            config={actionsConfig}
            onHideTooltip={handleHideTooltip}
            isSingleStepRemove={isSingleStepRemove}
            removeActionTitle={removeActionTitle}
          />
        )}
      </ReactTooltip>
    );
  };

  return (
    <span
      className={cnActionsMenu}
      onClickCapture={() => ReactTooltip.hide()}
      onClick={handleClick}
      tabIndex={0}
      role="button"
    >
      <span
        className={cnActionsIcon}
        data-for={String(id)}
        data-tip=""
        data-event="click"
        role="button"
        tabIndex={0}
        data-testid={testId}
      >
        <SVGIcon icon={icon} size={iconSize} />
      </span>
      {portalSelector
        ? createPortal(getTooltip(), document.querySelector(portalSelector))
        : getTooltip()}
    </span>
  );
}

export default ActionsMenu;
